import { css, cx } from "@emotion/css";
import { borderRadius, borderWidth, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import React from "react";
import { accessibilityStyles } from "../../utils";
import type { AvatarUserProps, AvatarSpaceProps, AvatarProjectProps, AvatarTenantProps, AvatarTemplateProps } from "../Avatar";
import { Avatar } from "../Avatar";
export type AvatarStackUserProps = Omit<AvatarUserProps, "size"> & {
    title: string;
};
type AvatarStackSpaceProps = Omit<AvatarSpaceProps, "size"> & {
    title: string;
};
type AvatarStackProjectProps = Omit<AvatarProjectProps, "size"> & {
    title: string;
};
type AvatarStackTenantProps = Omit<AvatarTenantProps, "size"> & {
    title: string;
};
type AvatarStackTemplateProps = Omit<AvatarTemplateProps, "size"> & {
    title: string;
};
export type AvatarStackEntityProps = AvatarStackSpaceProps | AvatarStackProjectProps | AvatarStackTenantProps | AvatarStackTemplateProps;
function isUserAvatar(avatar: AvatarStackUserProps | AvatarStackEntityProps): avatar is AvatarStackUserProps {
    return avatar.type === "user";
}
function isEntityAvatar(avatar: AvatarStackUserProps | AvatarStackEntityProps): avatar is AvatarStackEntityProps {
    return ["space", "project", "tenant", "template"].includes(avatar.type);
}
export interface AvatarStackProps {
    /** Array of avatar props to render in the stack (size will be set by AvatarStack, title is required for AvatarStack) */
    avatars: (AvatarStackUserProps | AvatarStackEntityProps)[];
    /** The URL to navigate to when the avatar stack is clicked */
    href?: string;
}
/**
 * AvatarStack Component
 * Displays multiple avatars in a horizontal stack with overlap.
 * Limits the number of rendered avatars to 3.
 * Automatically detects avatar types and ensures consistency (either all user OR all entity types, never both).
 * Note: The title prop is required for all avatars in the stack to ensure proper accessibility.
 *
 * @param {AvatarStackProps} props - The properties for the avatar stack component.
 * @param {(AvatarStackUserProps | AvatarStackEntityProps)[]} props.avatars - Array of avatar props to render in the stack (size will be set by AvatarStack, title is required for AvatarStack usage).
 * @param {string} props.href - The URL to navigate to when the avatar stack is clicked.
 * @returns {React.ReactElement} A React element representing the avatar stack.
 */
export function AvatarStack({ avatars, href }: AvatarStackProps): React.ReactElement {
    if (avatars.length === 1) {
        return <Avatar {...avatars[0]} size="medium" href={href}/>;
    }
    if (href) {
        return (<a href={href} className={rootAvatarStackStyles}>
                <AvatarStackItems avatars={avatars}/>
            </a>);
    }
    return (<ul className={rootAvatarStackStyles}>
            <AvatarStackItems avatars={avatars}/>
        </ul>);
}
function AvatarStackItems({ avatars }: {
    avatars: (AvatarStackUserProps | AvatarStackEntityProps)[];
}): React.ReactElement {
    const avatarCount = avatars.length > 3 ? `+${avatars.length - 3}` : null;
    const entityType = isEntityAvatar(avatars[0]);
    const avatarStackItems = avatars &&
        avatars.slice(0, 3).map((avatar, index) => (<li className={cx(avatarWrapperStyles, { [entityTypeStyles]: entityType })} key={`${avatar.type}-${index}`}>
                <Avatar {...avatar} size="medium"/>
            </li>));
    const pluralText = avatars && avatars.length > 4 ? "s" : "";
    const accessibleText = isUserAvatar(avatars[0]) ? `user${pluralText}` : "more";
    return (<>
            {avatarStackItems}
            {avatarCount && (<p className={countTextStyles}>
                    {avatarCount} <span className={hiddenFromScreenReadersStyles}>{accessibleText}</span>
                </p>)}
        </>);
}
// Note: The design and code differ slightly because the avatar stack
// renders differently in Figma compared to code, even with matching pixel values.
// To compensate, we increased the border width to 4, which removes the need for padding.
const rootAvatarStackStyles = css({
    display: "grid",
    gridTemplateColumns: "repeat(4, auto)",
    alignItems: "center",
    justifyItems: "center",
    position: "relative",
    isolation: "isolate",
    borderRadius: borderRadius.small,
    width: "fit-content",
    boxSizing: "border-box",
    textDecoration: "none",
    "&[href]:hover": {
        cursor: "pointer",
        backgroundColor: themeTokens.color.avatarStack.background.hover,
        "& > li": {
            border: `${borderWidth[4]} solid ${themeTokens.color.avatarStack.background.hover}`,
        },
        "& > p": {
            textDecoration: "underline",
            textDecorationColor: themeTokens.color.text.link.hover,
            color: themeTokens.color.text.link.hover,
        },
    },
    "&[href]:focus-visible": {
        outline: `${borderWidth[2]} solid ${themeTokens.color.border.selected}`,
        outlineOffset: "0",
        backgroundColor: themeTokens.color.avatarStack.background.hover,
        "& > li": {
            border: `${borderWidth[4]} solid ${themeTokens.color.avatarStack.background.hover}`,
        },
    },
});
const avatarWrapperStyles = css({
    position: "relative",
    border: `${borderWidth[4]} solid ${themeTokens.color.background.supplementary.default}`,
    backgroundColor: themeTokens.color.background.supplementary.default,
    zIndex: 1,
    listStyle: "none",
    margin: 0,
    padding: 0,
    borderRadius: borderRadius.circle,
    boxSizing: "border-box",
    "&:not(:first-child)": {
        marginLeft: `-${space[12]}`,
    },
    "&:nth-child(1)": {
        zIndex: 3,
    },
    "&:nth-child(2)": {
        zIndex: 2,
    },
    "&:nth-child(3)": {
        zIndex: 1,
    },
});
const entityTypeStyles = css({
    borderRadius: borderRadius.large,
});
const countTextStyles = css({
    margin: 0,
    marginLeft: space[4],
    // Extra right margin added to balance the visual illusion of a larger left side in the avatar stack
    marginRight: space[6],
    color: themeTokens.color.text.link.default,
    font: text.body.regular.medium,
});
const hiddenFromScreenReadersStyles = css({
    ...accessibilityStyles.hidden,
});
